import { SelectionModel } from '@angular/cdk/collections';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonComponent } from '@modules/components/button/button.component';
import { SearchFieldComponent } from '@modules/components/search-field/search-field.component';
import { USER } from '@tokens/user.token';
import { CompanyCard } from '@type/company.type';
import { NamedId } from '@type/name-id.type';
import { UserPermissionValue, UserSaveRequest } from '@type/user.type';
import { firstValueFrom, map, startWith, switchMap, tap } from 'rxjs';
import { DictionaryService } from 'src/app/core/services/dictionary/dictionary.service';
import { CompaniesService } from '../../services/companies.service';
import { CompanyStateService } from '../../services/company-state.service';
import { UsersService } from '../users-list/services/users/users.service';
import { UserFormService } from './service/user-form.service';

@Component({
  selector: 'app-invite-dialog-user',
  imports: [
    AsyncPipe,
    MatDialogModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatInputModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    SearchFieldComponent,
    ButtonComponent,
  ],
  templateUrl: './invite-dialog-user.component.html',
  styleUrls: ['./invite-dialog-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteDialogUserComponent implements OnInit, OnDestroy {
  private usersApiService = inject(UsersService);

  private destroyRef = inject(DestroyRef);

  private dictionaryService = inject(DictionaryService);

  private companiesService = inject(CompaniesService);

  company = inject(CompanyStateService).company;

  #currentUser = inject(USER);

  showSqlQueryAccess = computed(() => {
    if (this.company()?.id) return false;
    return this.data.form
      ? this.data.form.sql_query_access !== undefined
      : this.#currentUser().sql_query_access !== undefined;
  });

  searchControl = new FormControl('', { nonNullable: true });

  data = inject(MAT_DIALOG_DATA);

  form = inject(UserFormService).form;

  selection: SelectionModel<CompanyCard> = new SelectionModel(true);

  usersRole = toSignal(
    this.dictionaryService
      .getDictionary(
        this.dictionaryService.createRequest({
          q: { role_type_eq: this.company()?.id && !this.data.form ? 'admin' : '' },
          model_name: 'role',
          company: this.company()?.id ? String(this.company()?.id) : '',
        }),
      )
      .pipe(map((res) => res['data'])),
    {
      initialValue: [],
    },
  );

  companiesList = signal<CompanyCard[]>([]);

  isDisabledSelectCompany = signal(false);

  ngOnInit() {
    this.loadCompaniesList();
    this.patchFormValue();
    this.subscribeSelectionChange();
  }

  ngOnDestroy(): void {
    this.form.reset();
  }

  isAllSelected() {
    return this.selection.selected.length === this.companiesList().length;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.deselect(...this.companiesList());
    } else {
      this.selection.select(...this.companiesList());
    }
  }

  private loadCompaniesList() {
    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(''),
        switchMap((query) => {
          return this.companiesService.getCompaniesList({ name_cont: query }).pipe(
            tap(({ companies }) => {
              this.companiesList.set(companies);
              this.setSelection();
              this.subscribeToChangeRoleType();
            }),
          );
        }),
      )
      .subscribe();
  }

  private patchFormValue() {
    if (this.data.form) {
      this.form.patchValue({
        first_name: this.data.form.first_name,
        last_name: this.data.form.last_name,
        email: this.data.form.email,
        role: this.data.form.role.name,
        companyIdList: this.data.form.available_company_ids?.map((c: NamedId) => c.id),
        sqlQueryAccess: this.data.form.sql_query_access ?? false,
      });
    }
  }

  private setSelection() {
    const selectedCompanyIdList = this.form.controls.companyIdList.value;
    const superAdminRole = this.form.controls.role.value === UserPermissionValue.SuperAdmin;

    if (this.companiesList()) {
      this.selection.select(
        ...this.companiesList().filter((company) => selectedCompanyIdList?.includes(company.id) || superAdminRole),
      );
    }
  }

  private subscribeSelectionChange() {
    this.selection.changed
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((res) => {
          this.form.controls.companyIdList.patchValue(res.source.selected.map((el) => el.id));
        }),
      )
      .subscribe();
  }

  private subscribeToChangeRoleType() {
    this.form.controls.role.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(this.form.controls.role.value),
        map((roleName) => {
          this.isDisabledSelectCompany.set(false);
          if (roleName === UserPermissionValue.SuperAdmin) {
            this.isDisabledSelectCompany.set(true);
            this.selection.select(...this.companiesList());
          }
        }),
      )
      .subscribe();
  }

  save() {
    if (this.form.invalid) return;

    const saveOptsUserRequest = (value: Partial<UserSaveRequest>) => {
      //TODO: think about how to use value as an object
      const role = this.usersRole().find((r) => {
        return r.name === value.role;
      });

      return {
        id: value?.id,
        first_name: value.first_name,
        last_name: value.last_name,
        email: value.email,
        role_id: role!.id,
        available_company_ids: value.companyIdList,
        ...(this.showSqlQueryAccess() && { sql_query_access: this.form.controls.sqlQueryAccess.value }),
      };
    };

    if (this.data.form) {
      firstValueFrom(
        this.usersApiService.updateUser(
          saveOptsUserRequest(Object.assign(this.data.form, this.form.value)),
          this.company()?.id,
        ),
      );
    } else {
      firstValueFrom(this.usersApiService.createUser(saveOptsUserRequest(this.form.value), this.company()?.id));
    }
  }
}
